import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, CheckCircle2, AlertCircle } from "lucide-react";
import { postMembership } from "../lib/api";
import PageHero from "../components/PageHero";
import { Kicker, Reveal } from "../components/motion";

const EMPTY = { full_name: "", email: "", country: "", institution: "", field_of_study: "", academic_level: "undergrad", motivation: "" };

export default function Join() {
  const { t } = useTranslation();
  const benefits = t("join.benefits", { returnObjects: true });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setStatus(null);
    try {
      await postMembership(form);
      setStatus("ok");
      setForm(EMPTY);
    } catch {
      setStatus("err");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="page-join">
      <PageHero
        testId="join-hero"
        kicker={t("join.kicker")}
        title={t("join.headline")}
        lead={t("join.lead")}
        aside={
          <div className="panel-forest rounded-[14px] p-8 lg:p-10" data-testid="join-benefits">
            <div className="relative">
              <Kicker light>{t("join.benefits_title")}</Kicker>
              <ul className="mt-6 space-y-4">
                {Array.isArray(benefits) && benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/90">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-gold text-white flex items-center justify-center flex-shrink-0"><Check className="h-3 w-3" /></span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        }
      />

      <section className="surface-2" data-testid="join-form-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20 lg:py-24">
          <Reveal>
            <form onSubmit={onSubmit} className="paper p-8 lg:p-14 space-y-8" data-testid="join-form">
              <Kicker>{t("join.kicker")}</Kicker>
              <h2 className="font-display text-3xl lg:text-4xl text-ink">{t("common.apply")}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 mt-6">
                <div>
                  <label className="gxr-label" htmlFor="j-name">{t("join.form.name")} *</label>
                  <input id="j-name" required value={form.full_name} onChange={set("full_name")} className="gxr-input" data-testid="join-name-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="j-email">{t("join.form.email")} *</label>
                  <input id="j-email" type="email" required value={form.email} onChange={set("email")} className="gxr-input" data-testid="join-email-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="j-country">{t("join.form.country")} *</label>
                  <input id="j-country" required value={form.country} onChange={set("country")} className="gxr-input" data-testid="join-country-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="j-inst">{t("join.form.institution")}</label>
                  <input id="j-inst" value={form.institution} onChange={set("institution")} className="gxr-input" data-testid="join-institution-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="j-field">{t("join.form.field")}</label>
                  <input id="j-field" value={form.field_of_study} onChange={set("field_of_study")} className="gxr-input" data-testid="join-field-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="j-level">{t("join.form.level")}</label>
                  <select id="j-level" value={form.academic_level} onChange={set("academic_level")} className="gxr-input bg-transparent" data-testid="join-level-select">
                    {["undergrad", "masters", "phd", "researcher", "other"].map((k) => (
                      <option key={k} value={k}>{t(`join.form.level_options.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="gxr-label" htmlFor="j-motiv">{t("join.form.motivation")} *</label>
                  <textarea id="j-motiv" required rows={5} value={form.motivation} onChange={set("motivation")} className="gxr-input resize-y" data-testid="join-motivation-input" />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 flex-wrap">
                <button type="submit" disabled={submitting} className="btn-forest" data-testid="join-submit-button">
                  {submitting ? t("common.sending") : t("common.apply")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                {status === "ok" && (
                  <div className="flex items-center gap-2 text-forest" data-testid="join-success"><CheckCircle2 className="h-5 w-5" /> {t("common.thankYou")}</div>
                )}
                {status === "err" && (
                  <div className="flex items-center gap-2 text-red-600" data-testid="join-error"><AlertCircle className="h-5 w-5" /> {t("common.errorGeneric")}</div>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
